import { getAddress } from '@ethersproject/address'
import { JsonRpcSigner, Web3Provider } from '@ethersproject/providers'
import { BigNumber } from '@ethersproject/bignumber'
import { AddressZero } from '@ethersproject/constants'
import { Contract } from '@ethersproject/contracts'
import { TokenAddressMap } from 'config/swap/types'
import { Chain } from 'config/constants/types'
import { Percent } from 'swap-sdk/entities/fractions/percent'
import { CurrencyAmount } from 'swap-sdk/entities/fractions/currencyAmount'
import { Currency } from 'swap-sdk/entities/currency'
import { TokenClass } from 'swap-sdk/entities/token'
import { type PublicClient, type HttpTransport } from 'viem'
import { usePublicClient } from 'wagmi'

import JSBI from 'jsbi'
import { ethers, providers } from 'ethers'
import { BASE_BSC_SCAN_URLS } from '../config'
import React from 'react'

// returns the checksummed address if the address is valid, otherwise returns false
export function isAddress(value: any): string | false {
  try {
    return getAddress(value)
  } catch {
    return false
  }
}

export function getScanText(chainId: Chain = Chain.BSC_MAINNET) {
  switch (chainId) {
    case Chain.BSC_MAINNET:
      return 'BscScan'
    case Chain.BSC_TESTNET:
      return 'BscScan'
    case Chain.ETH_MAINNET:
      return 'EtherScan'
    case Chain.MOONRIVER_MAINNET:
      return 'MoonScan'
    case Chain.POLYGON_MAINNET:
      return 'PolygonScan'
    case Chain.AVAX_MAINNET:
      return 'Snowtrace'
    case Chain.MOONBEAM_MAINNET:
      return 'MoonScan'
    case Chain.AURORA_MAINNET:
      return 'AuroraScan'
    case Chain.ARBITRUM_MAINNET:
      return 'Arbiscan'
    case Chain.ARBITRUM_TESTNET:
      return 'Arbiscan'
    case Chain.OPTIMISM_MAINNET:
      return 'Etherscan'
    case Chain.OPTIMISM_TESTNET:
      return 'Etherscan'
    case Chain.NEON_DEVNET:
      return 'Neonscan'
    case Chain.OKC_MAINNET:
      return 'OKLink'
    case (Chain.GOERLI, Chain.ETH_MAINNET):
      return 'Etherscan'
    default:
      return 'BlockExplorer'
  }
}

export function getBscScanLink(
  data: string | number,
  type: 'transaction' | 'token' | 'address' | 'block' | 'countdown',
  chainId: Chain = Chain.BSC_MAINNET,
): string {
  switch (type) {
    case 'transaction': {
      return `${BASE_BSC_SCAN_URLS[chainId]}/tx/${data}`
    }
    case 'token': {
      return `${BASE_BSC_SCAN_URLS[chainId]}/token/${data}`
    }
    case 'block': {
      return `${BASE_BSC_SCAN_URLS[chainId]}/block/${data}`
    }
    case 'countdown': {
      return `${BASE_BSC_SCAN_URLS[chainId]}/block/countdown/${data}`
    }
    default: {
      return `${BASE_BSC_SCAN_URLS[chainId]}/address/${data}`
    }
  }
}

export function getBscScanLinkForNft(
  collectionAddress: string,
  tokenId: string,
  chainId: Chain = Chain.BSC_MAINNET,
): string {
  return `${BASE_BSC_SCAN_URLS[chainId]}/token/${collectionAddress}?a=${tokenId}`
}

// add 10%
export function calculateGasMargin(value: BigNumber): BigNumber {
  return value.mul(BigNumber.from(10000).add(BigNumber.from(1000))).div(BigNumber.from(10000))
}

// converts a basis points value to a sdk percent
export function basisPointsToPercent(num: number): Percent {
  return new Percent(JSBI.BigInt(num), JSBI.BigInt(10000))
}

export function calculateSlippageAmount(value: CurrencyAmount, slippage: number): [JSBI, JSBI] {
  if (slippage < 0 || slippage > 10000) {
    throw Error(`Unexpected slippage value: ${slippage}`)
  }
  return [
    JSBI.divide(JSBI.multiply(value.raw, JSBI.BigInt(10000 - slippage)), JSBI.BigInt(10000)),
    JSBI.divide(JSBI.multiply(value.raw, JSBI.BigInt(10000 + slippage)), JSBI.BigInt(10000)),
  ]
}

// account is not optional
export function getSigner(library: Web3Provider, account: string): JsonRpcSigner {
  return library.getSigner(account).connectUnchecked()
}

// account is optional
export function getProviderOrSigner(library: any, account?: string): Web3Provider | JsonRpcSigner {
  return account ? getSigner(library, account) : library
}

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export function isTokenOnList(defaultTokens: TokenAddressMap, currency?: Currency): boolean {
  if (currency.isNative) return true
  return Boolean(currency instanceof TokenClass && defaultTokens[currency.chainId]?.[currency.address])
}

export function publicClientToProvider(publicClient: PublicClient) {
  const { chain, transport } = publicClient
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  }
  if (transport.type === 'fallback')
    return new providers.FallbackProvider(
      (transport.transports as ReturnType<HttpTransport>[]).map(
        ({ value }) => new providers.JsonRpcProvider(value?.url, network),
      ),
    )
  return new providers.JsonRpcProvider(transport.url, network)
}

/** Hook to convert a viem Public Client to an ethers.js Provider. */
export function useEthersProvider({ chainId }: { chainId?: number } = {}) {
  const publicClient = usePublicClient({ chainId })
  // @ts-ignore
  return React.useMemo(() => publicClientToProvider(publicClient), [publicClient])
}

// account is optional
export function getContract(address: string, ABI: any, library: Web3Provider, account?: string): Contract {
  if (!isAddress(address) || address === AddressZero) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }
  const provider = new ethers.providers.Web3Provider(library as any)

  return new Contract(address, ABI, getProviderOrSigner(provider, account) as any)
}
